<script lang="ts">
	import { ListMenuCardV2, IconButton } from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { createEventDispatcher, onDestroy } from 'svelte';

	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { user_data } from 'src/stores/account/user';
	import VideoContainer from '../CameraPlayer/VideoContainer.svelte';
	import WebRTCPlayer from '../CameraPlayer/WebRTCPlayer.svelte';
	import CameraTileWidgetCard from './CameraTileWidgetCard.svelte';
	import { getDeviceLastSeenData } from 'src/helpers/device_state_view_helpers';
	import { computeCameraState, isCameraFirmwareUpdateRequired } from '../helpers/camera-state-helpers';
	import { hasActiveWebRTCSession } from '../helpers/webrtc-session-manager';
	import { getCameraComponentKey, isCameraPrivacyEnabled } from '../helpers/camera-helpers';
	import type { CameraStreamer } from '../types/camera-status';
	import { format } from 'svelte-i18n';
	import { capFirstChar } from 'src/helpers/utilities';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { load_image_from, real_id } from 'src/helpers/interface_helpers';
	import { requestVideoFullscreen } from '../helpers/camera-helpers';
	import { createThumbnailResolver } from '../helpers/encryption/recording-image-decryption';
	import type { Device } from 'src/types/Device';
	import type { WebRTCPlayerSession } from '../types/webrtc-session';
	import type { VideoContainerState } from '../types/video-container';

	export let camera: Device;
	export let editMode: boolean = false;
	export let mediaFit: 'cover' | 'contain' = 'cover';
	/** Dashboard embed: minimal chrome, ellipsis opens device drawer. */
	export let embedMode = false;
	/** Media only — no tile overlay (parent card supplies status chrome). */
	export let backgroundOnly = false;
	export let hideHeaderActions = false;
	export let hideBottomActions = false;
	/** Dashboard widgets: fill container height instead of fixed 16:9 only. */
	export let fillContainer = false;
	/** Semi-transparent status header (motion, battery, live, etc.). */
	export let showDashboardHeader = false;
	export let headerCompact = false;
	export let denseOverlay = false;
	export let showNameOnHover = false;
	export let useCloudConnectionIcon = false;
	/** Focus (spotlight) layout: show a button that promotes this tile to the main camera. */
	export let showFocusButton = false;
	/** Mount WebRTC when the camera is ready. Snapshot mode shows the thumbnail instead. */
	export let liveFeed = true;
	/** Native-video fullscreen control. */
	export let allowFullscreen = true;
	/** Merged camera + player state. Bind from a parent that draws its own status chrome. */
	export let videoState: VideoContainerState = null;
	/** True only while the player's `<video>` renders frames. Bind to gate a live badge. */
	export let isLive = false;

	const dispatch = createEventDispatcher();

	function onFocusClick(e: MouseEvent) {
		e.stopPropagation();
		dispatch('focus');
	}

	// Desktop/mobile detection for drag behavior
	let windowInnerWidth: number;
	$: isDesktop = windowInnerWidth > SystemScreenBreakpoints.TABLET_MAX;

	// Desktop: entire tile is draggable in edit mode
	function handleDesktopDragStart(e: MouseEvent | TouchEvent) {
		if (!editMode || !isDesktop) return;
		dispatch('handle-drag-start', e);
	}

	function handleDesktopDragEnd(e: MouseEvent | TouchEvent) {
		if (!editMode || !isDesktop) return;
		dispatch('handle-drag-end', e);
	}

	// Derived state (use real_id — status/settings are keyed like other dashboard cards)
	$: deviceId = real_id(camera.id);
	$: statusStore = getDeviceStatus(deviceId);
	$: settingsStore = getDeviceSettings(deviceId);
	$: status = $statusStore;
	$: settings = $settingsStore;
	$: cameraKey = getCameraComponentKey(camera);
	$: isOnline = status?.online ?? false;
	$: isPrivacyMode = isCameraPrivacyEnabled(camera, status, settings);
	$: isSupportAccess = $user_data?.temporary === true;
	$: streamer = (status?.[cameraKey] as { streamer?: CameraStreamer } | undefined)?.streamer;
	$: firmwareUpdateRequired = isCameraFirmwareUpdateRequired(camera, status);
	$: streams = (status?.[cameraKey] as { streams?: number } | undefined)?.streams;
	$: lastSeenData = getDeviceLastSeenData(camera, status, settings);

	let previewImage = '';

	// The resolver hands back a displayable URL already — a signed cloud URL, or a
	// blob URL for an encrypted thumbnail. Neither may be run through
	// `load_image_from`, which is for repo-relative paths.
	const previewResolver = createThumbnailResolver((url) => (previewImage = url));

	$: if (!isSupportAccess) {
		previewResolver.update({ deviceId, previewSize: 'medium' });
	} else {
		previewImage = '';
	}

	const defaultPlaceholderSrc = load_image_from('images/device_images/logo_white_S.png');

	function pickTilePreview(logo: boolean, imageless: boolean, url: string): string {
		if (logo) return defaultPlaceholderSrc;
		if (imageless) return '';
		return url;
	}

	onDestroy(() => {
		previewResolver.destroy();
	});

	// Tile sizing based on measured tile width (bind:clientWidth).
	// Accounts for sidebars, padding, and gaps automatically.
	type TileSize = 's' | 'm' | 'l';
	type AvatarSize = 's' | 'm' | 'm_plus' | 'l' | 'xl' | 'xxl';
	let tileWidth = 0;
	function getTileConfig(width: number): { size: TileSize; avatarSize: AvatarSize; compact: boolean } {
		if (width >= 500) return { size: 'l', avatarSize: 'xl', compact: false };
		if (width >= 300) return { size: 'm', avatarSize: 'l', compact: true };
		return { size: 's', avatarSize: 'm', compact: true };
	}
	$: tileConfig = getTileConfig(tileWidth);
	$: tileSize = tileConfig.size;
	$: avatarSize = tileConfig.avatarSize;
	$: compact = tileConfig.compact;

	// Format last seen time for display
	$: lastSeenTime = lastSeenData?.lastSeenUpdate
		? capFirstChar(
				$format('text_last_active', {
					default: `Last active: ${lastSeenData.time}.`,
					values: { time: lastSeenData.time },
				}),
			)
		: '';

	function handleOpenDevice() {
		setSidePanelState({ type: 'device', id: camera.id.toString(), open: true });
		dispatch('openDevice');
	}

	// The player owns the camera's session and reports its state back here.
	let session: WebRTCPlayerSession = null;

	// Blocked states (prevent WebRTCPlayer from mounting): support access /
	// offline / firmware / privacy / camera full / streamer not running. Stream
	// errors come from the player. `hasActiveWebRTCSession` reads the session map
	// and is not reactive, so it is called here rather than in its own `$:` — the
	// `streams` push is what re-evaluates this, and that is the only value the
	// session gates.
	$: cameraState = computeCameraState(
		isPrivacyMode,
		isOnline,
		streamer,
		firmwareUpdateRequired,
		streams,
		hasActiveWebRTCSession(deviceId),
		isSupportAccess,
	);
	$: videoState = cameraState ?? $session?.streamState ?? null;

	$: mountLivePlayer = liveFeed && !cameraState;
	$: isStreaming = !cameraState && videoState?.type !== 'error' && videoState?.type !== 'loading';
	// Snapshot mode has no player, so "nothing is blocking" is not enough: only
	// a `<video>` that has started counts as live.
	$: isLive = mountLivePlayer && !cameraState && !!$session?.playing;

	$: minimalBlockedState = backgroundOnly || embedMode || showDashboardHeader;
	// Only offline keeps its snapshot: every other blocked state — privacy,
	// firmware, support access, stream error, and the streamer transitions that
	// arrive as `starting` — would pass a stale frame off as a live one.
	$: pictureless =
		!!videoState &&
		videoState.type !== 'loading' &&
		!(videoState.type === 'offline' && previewImage);
	// A tile whose parent draws the status chrome fills the frame with the S-logo
	// instead. One that renders the state in its own center must stay imageless
	// there — VideoContainer reads any preview as a picture and drops the
	// placeholder face for a bottom-aligned label.
	$: useLogoPlaceholder = pictureless && minimalBlockedState;
	$: tilePreviewImage = pickTilePreview(useLogoPlaceholder, pictureless, previewImage);
	$: tilePreviewFit = useLogoPlaceholder ? 'contain' : mediaFit;

	// Fullscreen: query video element and request fullscreen
	let tileElement: HTMLDivElement;

	function handleFullscreen() {
		const videoElement = tileElement?.querySelector('video') as HTMLVideoElement | null;
		requestVideoFullscreen(videoElement);
	}
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

{#if editMode}
	<!-- Edit mode: uses ListMenuCardV2 for drag-and-drop -->
	<div
		class="camera-tile-root"
		class:fill-container={fillContainer}
		bind:clientWidth={tileWidth}
		role="presentation"
		on:mousedown={handleDesktopDragStart}
		on:mouseup={handleDesktopDragEnd}
		on:touchend={handleDesktopDragEnd}
	>
		<ListMenuCardV2
			class="camera-tile-edit-card {fillContainer ? 'fill-container' : ''} {!isOnline &&
			previewImage
				? 'is-offline'
				: ''} {!previewImage ? 'no-image' : ''}"
			title={camera.name}
			image={previewImage || defaultPlaceholderSrc}
			editMode={true}
			type="extended"
			a11yProps={{
				dndMobileHandleAriaLabel: $format('aria_drag_camera', { default: 'Drag to reorder' }),
			}}
			on:handle-drag-start
			on:handle-drag-end
		/>
	</div>
{:else}
	<!-- Normal mode: delegate to widget for presentation -->
	<div
		class="camera-tile-root"
		class:fill-container={fillContainer}
		bind:this={tileElement}
		bind:clientWidth={tileWidth}
	>
		{#if showFocusButton}
			<div class="camera-focus-btn">
				<IconButton
					icon="fas fa-expand"
					variant="secondary"
					size="s"
					aria-label={$format('button_focus_camera', { default: 'Focus this camera' })}
					on:click={onFocusClick}
				/>
			</div>
		{/if}
		<CameraTileWidgetCard
			name={camera.name}
			isOnline={isStreaming}
			size={tileSize}
			embedMode={embedMode || backgroundOnly}
			{fillContainer}
			{hideHeaderActions}
			hideBottomActions={hideBottomActions || backgroundOnly}
			hideOverlay={backgroundOnly}
			showFullscreen={!backgroundOnly && !editMode && allowFullscreen}
			{showDashboardHeader}
			headerDevice={backgroundOnly ? undefined : camera}
			headerStatus={status}
			headerSettings={settings}
			showLiveBadge={showDashboardHeader && isLive && !useCloudConnectionIcon}
			{isLive}
			{headerCompact}
			{denseOverlay}
			{showNameOnHover}
			overlayState={videoState}
			on:openDevice={handleOpenDevice}
			on:fullscreen={handleFullscreen}
		>
			<svelte:fragment slot="media">
				<VideoContainer
					state={videoState}
					previewImage={tilePreviewImage}
					previewImageFit={tilePreviewFit}
					lastSeenTime={backgroundOnly ? '' : lastSeenTime}
					{avatarSize}
					isZoomable={!embedMode && !backgroundOnly}
					hasZoomCtrlShortcut={!embedMode && !backgroundOnly}
					compact={compact || backgroundOnly}
					{minimalBlockedState}
					{fillContainer}
				>
					{#if mountLivePlayer}
						<WebRTCPlayer
							bind:session
							deviceId={camera.id}
							showControls={false}
							objectFit={mediaFit}
							{fillContainer}
							previewSize="medium"
						/>
					{:else if !cameraState}
						<img
							src={tilePreviewImage || defaultPlaceholderSrc}
							alt={camera.name}
							class="camera-tile-snapshot"
							style={`object-fit: ${mediaFit};`}
						/>
					{/if}
				</VideoContainer>
			</svelte:fragment>
		</CameraTileWidgetCard>
	</div>
{/if}

<style>
	/* Edit mode: ListMenuCardV2 overrides */
	:global(.camera-tile-edit-card.list-menu-card-v2) {
		min-height: unset !important;
		aspect-ratio: 16 / 9;
		width: 100%;
		height: 100%;
		flex-shrink: 1;
		border-radius: var(--radius-extra-big);
		overflow: hidden;
	}

	:global(.camera-tile-edit-card.list-menu-card-v2.fill-container) {
		aspect-ratio: unset;
		min-height: 0 !important;
	}

	:global(.camera-tile-edit-card.is-offline) {
		filter: grayscale(100%);
	}

	:global(.camera-tile-edit-card.no-image) {
		background-color: var(--color-2);
	}

	.camera-tile-root {
		position: relative;
		width: 100%;
		height: 100%;
		min-width: 0;
		min-height: 0;
	}

	.camera-tile-root.fill-container {
		min-height: 0;
	}

	/* Focus-layout promote button, overlaid on the left edge of small tiles. */
	.camera-focus-btn {
		position: absolute;
		left: var(--spacing-s);
		top: 50%;
		transform: translateY(-50%);
		z-index: 3;
	}

	.camera-tile-snapshot {
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}
</style>
